/**
 * External dependencies
 */
import { useEffect, useState, type ReactNode } from 'react';

/**
 * Internal dependencies
 */
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@Components/AlertDialog';
import { Button } from '@Components/Button';
import { Input } from '@Components/Input';
import { cn } from '@Utils/cn';

interface ConfirmationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: () => void | Promise<void>;
  match: string;
  title: string;
  description?: string;
  /** Extra body content rendered between the description and the match input. */
  children?: ReactNode;
  matchLabel?: string;
  submitLabel?: string;
  cancelLabel?: string;
  isLoading?: boolean;
  variant?: 'default' | 'destructive';
}

export default function ConfirmationDialog({
  open,
  onOpenChange,
  onSubmit,
  match,
  title,
  description,
  children,
  matchLabel = 'Type to confirm',
  submitLabel = 'Confirm',
  cancelLabel = 'Cancel',
  isLoading = false,
  variant = 'default',
}: ConfirmationDialogProps) {
  const [inputValue, setInputValue] = useState('');

  const isMatch = inputValue.trim().toLowerCase() === match.trim().toLowerCase();

  useEffect(() => {
    if (!open) {
      setInputValue('');
    }
  }, [open]);

  const handleSubmit = async () => {
    if (!isMatch || isLoading) return;
    await onSubmit();
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          {description && (
            <AlertDialogDescription>{description}</AlertDialogDescription>
          )}
        </AlertDialogHeader>

        {children}

        <div>
          <label className="mb-2 block text-sm font-medium">{matchLabel}</label>
          <Input
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder={match}
            disabled={isLoading}
          />
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel disabled={isLoading}>{cancelLabel}</AlertDialogCancel>
          <Button
            onClick={handleSubmit}
            disabled={!isMatch || isLoading}
            className={cn(
              variant === 'destructive' && 'bg-red-600 hover:bg-red-700'
            )}
          >
            {isLoading ? `${submitLabel}...` : submitLabel}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
